import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useActionItems } from "@/hooks/use-action-items";
import { useTaskAction } from "@/hooks/use-task-action";
import { TaskCard } from "@/components/tasks/TaskCard";
import { TaskDetailDrawer } from "@/components/tasks/TaskDetailDrawer";

/**
 * BW19 PR-E + BW20.1 PR-C — Today's tasks section on /today.
 *
 * Shows up to 5 open tasks scheduled for today OR with due_at <= today.
 * Both done and cancelled hide here; they're visible in /tasks behind
 * "Show done" / "Show cancelled" filters.
 *
 * Tick = "I did it" (state='done'). Hover X / mobile swipe-left =
 * "Doesn't apply anymore" (state='cancelled'). Both trigger a 3-second
 * undo toast in State / Meaning / Next-move format. Row fades out
 * (200ms) via framer-motion exit animation.
 */

const VISIBLE_LIMIT = 5;

export function TodayTasksSection() {
  const { data, isLoading } = useActionItems();
  const { onCycleState, onCancel } = useTaskAction();
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  const todayItems = useMemo(() => {
    if (!data) return [];
    const today = startOfLocalDay(new Date());
    const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
    return data.filter((it) => {
      if (it.state === "done" || it.state === "cancelled") return false;
      if (it.scheduled_at) {
        const s = new Date(it.scheduled_at);
        if (s >= today && s < tomorrow) return true;
      }
      if (it.due_at) {
        const d = new Date(it.due_at);
        if (d < tomorrow) return true;
      }
      return false;
    });
  }, [data]);

  if (isLoading) return null;
  if (todayItems.length === 0) return null;

  const visible = todayItems.slice(0, VISIBLE_LIMIT);
  const moreCount = todayItems.length - visible.length;

  return (
    <section
      aria-label="Today's tasks"
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "var(--space-3)",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "var(--fg)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <ListChecks size={16} />
          Today's tasks
        </span>
        <Link
          to="/tasks"
          style={{ color: "var(--fg-2)", fontSize: 13, textDecoration: "none" }}
        >
          Open /tasks
        </Link>
      </header>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <AnimatePresence initial={false}>
          {visible.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: "hidden" }}
            >
              <TaskCard
                item={item}
                onCycleState={(next) => void onCycleState(item, next)}
                onCancel={() => void onCancel(item)}
                onOpen={() => setOpenTaskId(item.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {moreCount > 0 && (
          <Link
            to="/tasks"
            style={{
              color: "var(--fg-2)",
              fontSize: 13,
              alignSelf: "flex-start",
              textDecoration: "none",
            }}
          >
            +{moreCount} more
          </Link>
        )}
      </div>
      <TaskDetailDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
    </section>
  );
}

function startOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
